import type { DisplayMessageSnapshot } from '@/domain';
import type { StationId } from '@/domain/types';
import type { DisplayMessageRepository } from '@/persistence/ports';
import type { Evse3Database, DisplayMessageRow } from '../db';

/**
 * Session 19d — Dexie-backed OCPP 2.0.1 Display Messages store, keyed by
 * `[stationId+id]`. `upsert` replaces a message with the same id (the
 * spec's SetDisplayMessage replace semantics).
 */
export class DisplayMessageRepositoryDexie implements DisplayMessageRepository {
  constructor(private readonly db: Evse3Database) {}

  async byStation(stationId: StationId): Promise<readonly DisplayMessageSnapshot[]> {
    const rows = await this.db.displayMessages.where('stationId').equals(stationId).toArray();
    return rows.map(strip);
  }

  async upsert(stationId: StationId, snap: DisplayMessageSnapshot): Promise<void> {
    await this.db.displayMessages.put({ stationId, ...snap });
  }

  async delete(stationId: StationId, id: number): Promise<boolean> {
    const key: [StationId, number] = [stationId, id];
    const existing = await this.db.displayMessages.get(key);
    if (existing === undefined) return false;
    await this.db.displayMessages.delete(key);
    return true;
  }

  async clear(stationId: StationId): Promise<void> {
    await this.db.displayMessages.where('stationId').equals(stationId).delete();
  }
}

function strip(row: DisplayMessageRow): DisplayMessageSnapshot {
  const { stationId: _stationId, ...rest } = row;
  return rest;
}
